import { useState, useCallback } from 'react'
import type * as jsforce from 'jsforce'
import {
  getCurrentUserSyncSetting,
  getAuthenticationUrl,
  testAuthentication,
  toggleSync,
  syncNow,
} from '../services/salesforce'

/**
 * Google Calendar同期機能管理フック
 *
 * 責務:
 * - Google Calendar認証ダイアログの開閉状態管理
 * - 初回認証チェック・自動同期処理
 */
export function useGoogleCalendarSync(onSuccess: (message: string) => void, onError?: (message: string) => void) {
  const [googleAuthDialogOpen, setGoogleAuthDialogOpen] = useState(false)

  /**
   * Google Calendar認証チェックと自動同期を実行
   * ページロード時に呼び出される
   */
  const checkAndSync = useCallback(
    async (conn: jsforce.Connection) => {
      try {
        const syncSetting = await getCurrentUserSyncSetting(conn)

        if (
          syncSetting.authenticationStatus === 'Named Credential Not Configured' ||
          syncSetting.authenticationStatus === 'Not Authenticated'
        ) {
          // 認証されていない、またはNamed Credential未設定の場合、ダイアログ表示
          setGoogleAuthDialogOpen(true)
        } else if (syncSetting.authenticationStatus === 'Authenticated') {
          // 認証済みの場合
          if (!syncSetting.syncEnabled) {
            // 同期がOFFの場合、自動的にON
            console.log('Google Calendar同期を自動的に有効化します')
            await toggleSync(conn, true)
            onSuccess('Google Calendar同期を有効化しました')
          }

          // 同期実行（認証済みの場合は必ず同期を実行）
          console.log('Google Calendar同期を実行します')
          await syncNow(conn)
          console.log('Google Calendar同期が完了しました')
        }
      } catch (gcError) {
        // Google Calendar連携エラーは致命的ではないため、ログのみ
        const errorMessage = gcError instanceof Error ? gcError.message : String(gcError)

        // Named Credential未設定エラーの場合もダイアログ表示
        if (
          errorMessage.includes('named credential') ||
          errorMessage.includes("You don't have permission to view this data") ||
          errorMessage.includes('Named Credential Not Configured') ||
          errorMessage.includes('APEX_ERROR')
        ) {
          console.log('Google Calendar連携: Named Credentialが未設定です')
          setGoogleAuthDialogOpen(true)
        } else {
          // その他のエラーはログ出力
          console.error('Google Calendar連携エラー:', gcError)
          if (onError) {
            onError('Google Calendar連携エラーが発生しました')
          }
        }
      }
    },
    [onSuccess, onError],
  )

  /**
   * 認証URL取得ハンドラ（GoogleAuthDialogから呼ばれる）
   */
  const handleGetAuthUrl = useCallback(async (conn: jsforce.Connection) => {
    return await getAuthenticationUrl(conn)
  }, [])

  /**
   * 認証完了ハンドラ（GoogleAuthDialogから呼ばれる）
   */
  const handleAuthComplete = useCallback(
    async (conn: jsforce.Connection) => {
      const result = await testAuthentication(conn)
      if (!result.success) {
        throw new Error('認証が完了していません')
      }
      // 認証成功後、同期をONにして実行
      await toggleSync(conn, true)
      onSuccess('Google Calendar同期を有効化しました')
      await syncNow(conn)
      onSuccess('Google Calendar同期が完了しました')
    },
    [onSuccess],
  )

  return {
    googleAuthDialogOpen,
    setGoogleAuthDialogOpen,
    checkAndSync,
    handleGetAuthUrl,
    handleAuthComplete,
  }
}
